import React, { useMemo } from "react";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const parse = (key) => {
  const [y, m] = key.split("-").map(Number);
  return { y, m };
};

const shift = (key, delta) => {
  const { y, m } = parse(key);
  let ny = y, nm = m + delta;
  while (nm < 1) { nm += 12; ny -= 1; }
  while (nm > 12) { nm -= 12; ny += 1; }
  return `${ny}-${String(nm).padStart(2, "0")}`;
};

const PeriodPicker = ({ value, onChange, years = 4 }) => {
  const current = value || todayKey();
  const today = todayKey();
  const { y, m } = parse(current);

  const yearOptions = useMemo(() => {
    const thisYear = new Date().getFullYear();
    return Array.from({ length: years }, (_, i) => thisYear - i);
  }, [years]);

  const setMonth = (newM) => onChange(`${y}-${String(Number(newM)).padStart(2, "0")}`);
  const setYear = (newY) => onChange(`${newY}-${String(m).padStart(2, "0")}`);

  return (
    <div className="flex items-center gap-2" data-testid="period-picker">
      <button
        data-testid="period-prev"
        onClick={() => onChange(shift(current, -1))}
        className="w-8 h-8 flex items-center justify-center border border-[rgba(42,28,21,0.18)] text-[#14281E] hover:bg-[#2A1C15]/5"
        title="Previous month"
      >
        <CaretLeft size={14} weight="bold" />
      </button>

      <div className="flex items-center gap-1 bg-[#E7DDCB] border border-[rgba(42,28,21,0.18)] px-2 py-1.5">
        <CalendarBlank size={14} className="text-[#6E5022]" weight="bold" />
        <select
          data-testid="period-month"
          value={m}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-transparent text-[#14281E] font-body text-sm focus:outline-none cursor-pointer"
        >
          {MONTHS.map((label, i) => (
            <option key={label} value={i + 1}>{label}</option>
          ))}
        </select>
        <select
          data-testid="period-year"
          value={y}
          onChange={(e) => setYear(e.target.value)}
          className="bg-transparent text-[#14281E] font-body text-sm focus:outline-none cursor-pointer bj-tnum"
        >
          {yearOptions.map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </select>
      </div>

      <button
        data-testid="period-next"
        onClick={() => onChange(shift(current, 1))}
        className="w-8 h-8 flex items-center justify-center border border-[rgba(42,28,21,0.18)] text-[#14281E] hover:bg-[#2A1C15]/5"
        title="Next month"
      >
        <CaretRight size={14} weight="bold" />
      </button>

      {current !== today && (
        <button
          data-testid="period-today"
          onClick={() => onChange(today)}
          className="px-2.5 py-1 ml-1 border border-[#14281E] text-[#14281E] hover:bg-[#14281E] hover:text-[#F2EBDD] font-body tracking-widest text-[0.75rem] uppercase transition-colors"
        >
          Today
        </button>
      )}
    </div>
  );
};

// Delta indicator: green up arrow if increased, burgundy down if decreased
export const DeltaPill = ({ current, previous, isCurrency, invertColors = false }) => {
  if (previous === undefined || previous === null) return null;
  const diff = (current || 0) - (previous || 0);
  if (diff === 0) {
    return <span className="text-[#5E4D40] text-[0.75rem] tracking-widest uppercase font-body">No change vs prev</span>;
  }
  const pct = previous > 0 ? Math.round((diff / previous) * 100) : null;
  const up = diff > 0;
  // For "good" metrics (revenue collected, new clients), up=green.
  // For "bad" metrics (overdue), up=burgundy. Caller sets invertColors=true.
  const good = invertColors ? !up : up;
  const color = good ? "#14281E" : "#5C1A24";
  const arrow = up ? "▲" : "▼";
  const sign = isCurrency ? `${up ? "+" : "−"}$${Math.abs(diff).toLocaleString()}` : `${up ? "+" : "−"}${Math.abs(diff).toLocaleString()}`;
  return (
    <div className="flex items-center gap-1 text-[0.7rem] font-body bj-tnum" style={{ color }}>
      <span>{arrow}</span>
      <span>{sign}</span>
      {pct !== null && <span className="text-[#5E4D40]">({up ? "+" : "−"}{Math.abs(pct)}%)</span>}
    </div>
  );
};

export default PeriodPicker;
